import { borderRadius, colors } from '@/theme'
import type { TimelineDateGroup, TimelineEvent } from '@/types/timeline'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { Box, IconButton, Paper, Typography } from '@mui/material'
import { formatDate } from '@/lib/utils'
import TimelineEventCard from './TimelineEventCard'

interface TimelineListProps {
  entityName: string
  events: TimelineEvent[]
  totalItems: number
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  patientHash?: string
}

const ICON_CENTER_X = 30

function groupEventsByDate(events: TimelineEvent[]): TimelineDateGroup[] {
  const groups = new Map<string, TimelineEvent[]>()

  for (const event of events) {
    const key = formatDate(event.timestamp)
    const list = groups.get(key) ?? []
    list.push(event)
    groups.set(key, list)
  }

  return Array.from(groups.entries()).map(([date, events]) => ({ date, events }))
}

export default function TimelineList({
  entityName,
  events,
  totalItems,
  page,
  totalPages,
  onPageChange,
  patientHash
}: TimelineListProps) {
  const dateGroups = groupEventsByDate(events)

  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${colors.border.default}`, borderRadius: borderRadius.lg, p: 4, pb: 3 }}
    >
      <Typography sx={{ fontSize: '24px', fontWeight: 600, color: colors.text.primary, mb: 0.5 }}>
        {entityName}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: colors.text.muted, mb: 3 }}>
        Showing {events.length} of {totalItems} items
      </Typography>

      {dateGroups.map((group, groupIdx) => {
        const isLastGroup = groupIdx === dateGroups.length - 1
        return (
          <Box key={group.date}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.text.primary, mb: 3 }}>
              {group.date}
            </Typography>

            {group.events.map((event, idx) => {
              const isLast = idx === group.events.length - 1
              return (
                <Box key={event.id}>
                  <TimelineEventCard event={event} patientHash={patientHash} />
                  {!isLast && (
                    <Box
                      sx={{
                        width: 2,
                        height: 36,
                        ml: `${ICON_CENTER_X}px`,
                        mb: '9px',
                        bgcolor: colors.border.default,
                        borderRadius: 1
                      }}
                    />
                  )}
                </Box>
              )
            })}

            {!isLastGroup && (
              <Box
                sx={{
                  width: 2,
                  height: 36,
                  ml: `${ICON_CENTER_X}px`,
                  bgcolor: colors.border.default,
                  borderRadius: 1
                }}
              />
            )}
          </Box>
        )
      })}

      {events.length === 0 && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '14px', color: colors.text.muted }}>No timeline events found</Typography>
        </Box>
      )}

      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            pt: 3,
            mt: 2
          }}
        >
          <Typography sx={{ fontSize: '13px', color: colors.text.secondary }}>
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            size='small'
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            sx={{ color: colors.text.secondary }}
          >
            <ChevronLeft fontSize='small' />
          </IconButton>
          <IconButton
            size='small'
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            sx={{ color: colors.text.secondary }}
          >
            <ChevronRight fontSize='small' />
          </IconButton>
        </Box>
      )}
    </Paper>
  )
}
